<template>
    <div :id="id">
      <div v-if="showForm" class="mb-5" @click="() => checkAuth()">
        <div class="flex flex-row items-start justify-between space-x-2">
          <div class="relative w-full">
            <TextArea
              v-model="comment"
              name="comment"
              variant="outline"
              :placeholder="$t(placeholder)"
              class-name="w-full"
              :rows="2"
              :min-height="64"
              :input-class-name="auth?.idSupplier ? 'pe-16' : 'pe-8'"
              :on-submit="handleSubmit"
              @cursor-changed="onCursorChanged"
            />
            <ChatEmojiPicker
              v-model="comment"
              :class="[windowSize?.width?.value < 1024 ? 'top-2' : 'top-3', auth.isLoggedIn ? 'end-10' : 'end-2']"
              :cursor-position="cursorPosition"
              @cursor-changed="onCursorChanged"
            />
            <ChatActionMenu
              v-if="auth?.isLoggedIn"
              v-model="comment"
              :images="images"
              :class="windowSize?.width?.value < 1024 ? 'top-2' : 'top-3'"
              :cursor-position="cursorPosition"
              @cursor-changed="onCursorChanged"
            />
          </div>
          <Button
            variant="submit"
            :loading="sending"
            :disabled="!sendingEnabled"
            :title="$t('text-send')"
            :size="windowSize?.width?.value < 1024 ? 'small' : 'medium'"
            :on-click="handleSubmit"
          >
            <SendIcon :class="windowSize?.width?.value < 1024 ? 'w-4 h-4' : 'w-6 h-6'" />
          </Button>
        </div>
        <div v-if="!!images.length" class="flex flex-wrap mt-2">
          <div
            v-for="(file, idx) in images"
            :key="idx"
            class="inline-flex flex-col overflow-hidden mt-2 me-2 relative"
          >
            <div class="flex-none w-24 h-24 sm:w-48 sm:h-48 mr-2 mb-2">
              <img :src="file.url" class="rounded object-cover w-24 h-24 sm:w-48 sm:h-48" loading="lazy" >
            </div>
          </div>
        </div>
      </div>
      <div
        v-for="(item, idx) in messages.items"
        :key="item.id"
        class="w-full"
      >
        <SupplierDivider
          v-if="isSupplierOwnMailbox"
          :item="item"
          :prev-item="prevItem(idx)"
          :post-error="postError"
          :is-add-blocked="isAddBlocked"
        />
        <div :class="[ 'w-full flex', auth.idCustomer === item?.customer?.id ? 'justify-end' : 'justify-start' ]">
          <RightMessage
            v-if="auth.idCustomer === item?.customer?.id"
            :item="item"
            :prev-item="prevItem(idx)"
            :selected="selected"
            :parent-id="parent.id"
            :type="type"
          />
          <LeftMessage
            v-else
            :item="item"
            :prev-item="prevItem(idx)"
            :selected="selected"
            :is-read="canRead ? item.isRead : true"
            :parent-id="parent.id"
            :type="type"
            :reply-url="type === CHAT_MESSAGE_TYPE.SUPPLIER ? getConversationUrl(item) : null"
            @read="readMessage"
          />
        </div>
        <div v-if="hasConversationPreviewLink(item)" class="flex justify-center mt-4 lg:mt-6">
          <Button
            :loading="loadingMore"
            :disabled="loadingMore"
            variant="outline"
            class-name="text-sm md:text-base font-semibold h-11"
            @click="navigatorTo(getConversationUrl(item))"
          >
              {{ $t("text-show-all-messages") }}
          </Button>
        </div>
      </div>
      <div v-if="!showForm && type === CHAT_MESSAGE_TYPE.SUPPLIER && !messages.items.length" class="text-center text-body text-sm">
          {{ $t('text-no-messages-to-supplier') }}
      </div>
      <div v-if="loading && ui.displayModal" class="w-full flex justify-center items-center h-48 bg-transparent relative">
          <Spinner :text="$t('common:text-loading')" />
      </div>
      <div v-if="hasNextPage || loadingMore" class="flex justify-center mt-8 lg:mt-12">
        <Button
            :loading="loadingMore"
            :disabled="loadingMore"
            class-name="text-sm md:text-base font-semibold h-11"
            @click="handleLoadMore"
        >
            {{ $t("text-load-more") }}
        </Button>
      </div>
    </div>
</template>

<script setup>
import * as Sentry from "@sentry/vue";
import _ from 'lodash-es';
import { ROUTES } from "@utils/routes";
import { CHAT_MESSAGE_TYPE, STATUS_BLOCKED } from "@utils/constants";
import { useAuthStore } from '@stores/auth';
import { useCustomerStore } from '@stores/customer';
import { useUIStore } from '@stores/ui';
import Spinner from "@components/ui/loaders/spinner/spinner";
import SendIcon from "@components/icons/send-icon";
import TextArea from "@components/ui/text-area";
import Button from "@components/ui/button";
import LeftMessage from "@components/chat/left-message";
import RightMessage from "@components/chat/right-message";
import SupplierDivider from "@components/chat/supplier-divider";
import ChatActionMenu from "@components/chat/chat-action-menu";
import ChatEmojiPicker from "@components/chat/chat-emoji-picker";

const props = defineProps({
  id: {
    type: String,
    default: null
  },
  parent: {
    type: Object,
    required: true
  },
  type: {
    type: String,
    required: true
  },
  fetchUrl: {
    type: String,
    required: true
  },
  loadError: {
    type: String,
    default: ''
  },
  postError: {
    type: String,
    default: ''
  },
  showForm: {
    type: Boolean,
    default: true
  },
  placeholder: {
    type: String,
    default: 'text-add-comment'
  },
  isAddBlocked: {
    type: Boolean,
    default: false
  }
});

const auth = useAuthStore();
const customer = useCustomerStore();
const ui = useUIStore();
const route = useRoute();
const routeLang = useRouteLang();

const { t } = useI18n();
const { $eventBus, $toast } = useNuxtApp();
const loading = useState(`chat-loading:${props.type}:${props.parent.id}`, () => false);
const loadingMore = ref(false);
const sending = ref(false);
const selected = ref(null);
const images = ref([]);
const comment = ref('');
const messages = useState(`chat-messages:${props.type}:${props.parent.id}`, () => ({ items: [] }));
const pagination = useState(`chat-pagination:${props.type}:${props.parent.id}`, () => ({ page: 1, pageCount: 1, itemsPerPage: 20, itemCount: 0 }));
const toRead = ref([]);
const cursorPosition = ref(0);

const windowSize = useWindowSize();

const addMessage = (data) => {
  if (!_.find(messages.value.items, { id: data.id })) {
    data.insertedAt = null;
    const targetSupplierId = data?.supplier?.id !== auth.idSupplier
      ? data?.supplier?.id
      : (data?.toSupplier?.id !== auth.idSupplier ? data?.toSupplier?.id : null);

    const insertIndex = messages.value.items.findIndex((item) => (
      (item?.supplier?.id !== auth.idSupplier && item?.supplier?.id === targetSupplierId)
      || (item?.toSupplier?.id !== auth.idSupplier && item?.toSupplier?.id === targetSupplierId)
    ));

    if (insertIndex >= 0) {
      messages.value.items.splice(insertIndex, 0, data);
    } else {
      messages.value.items.unshift(data);
    }

    $eventBus.emit(`${props.type}:update`, {
      id: props.parent.id,
      messagesCount: (props.parent?.messagesCount || 0) + 1
    });
  }
}

const hasNextPage = computed(() => pagination.value.page < pagination.value.pageCount);

const isSupplierOwnMailbox = computed(() => props.type === CHAT_MESSAGE_TYPE.SUPPLIER && auth.idSupplier === props.parent?.id);

const getConversationSupplier = (item) => {
  if (!item) {
    return null;
  }

  if (isSupplierOwnMailbox.value && auth.idSupplier === item?.supplier?.id) {
    return item?.toSupplier || null;
  }

  if (isSupplierOwnMailbox.value && auth.idSupplier === item?.toSupplier?.id) {
    return item?.supplier || null;
  }

  return item?.supplier || item?.toSupplier || null;
};

const getConversationUrl = (item) => {
  const supplier = getConversationSupplier(item);

  return supplier?.id
    ? translatePath(ROUTES.CODE.SUPPLIER, slugify(supplier.id, supplier.name))
    : null;
};

const hasConversationPreviewLink = (item) => !!(isSupplierOwnMailbox.value && item?.hasMoreInConversation && getConversationUrl(item));

const prevItem = (idx) => idx ? messages.value.items[idx - 1] : ({ });

const sendingEnabled = computed(() => {
  return auth.isLoggedIn && !sending.value && (!!comment.value.length || !!images.value.length);
});

const reload = async (pending = loading) => {
  if (pending.value) {
    return;
  }
  pending.value = true;
  try {
    const data = await $fetch(props.fetchUrl, {
      method: 'GET',
      params: {
        page: pagination.value.page,
        limit: pagination.value.itemsPerPage,
        locale: routeLang.locale.value
      }
    });
    messages.value.items.push(...data?.items || []);
    pagination.value = data?.pagination || pagination.value;
  } catch (error) {
    if (import.meta.client) {
      $toast.error(t(props.loadError));
    }
    Sentry.captureException(error);
  }
  pending.value = false;
}

const handleLoadMore = async () => {
  pagination.value.page++;
  await reload(loadingMore);
}

const scrollToSelected = () => nextTick(() => {
  const element = document.querySelector(`[data-m-id='m-${selected.value}']`);
  if (element) {
    window.scrollTo({ top: (element?.offsetTop || 0) - 120, behavior: "smooth" });
  }
});

const handleLoadSelected = async () => {
  while (import.meta.client && selected.value && !_.find(messages.value?.items || [], { id: selected.value }) && (pagination.value.page < pagination.value.pageCount)) {
    await handleLoadMore();
  }
  scrollToSelected();
};

const reset = () => {
    comment.value = '';
    images.value = [];
}

const handleSubmit = async () => {
  if (props.isAddBlocked) {
    $toast.error(t('text-add-message-blocked-notice'));
    return;
  }
  sending.value = true;
  if (customer?.status === STATUS_BLOCKED || (auth?.idSupplier && customer?.supplier?.status === STATUS_BLOCKED)) {
    // $toast.error(t('customer.blocked.error'));
    return;
  }
  const body = {
    content: comment.value,
    images: images.value.map((image) => ({
      filename: image.name,
      content: image.encoded
    }))
  }
  try {
    const data = await $fetch(props.fetchUrl, {
      method: 'POST',
      body
    });
    reset();
    addMessage(data);
  } catch (error) {
    if (import.meta.client) {
      const errorMessage = {
        1100: 'error.direct-message.outreach-limit',
        1101: 'error.direct-message.recipient-without-listing'
      }[error?.data?.data?.code] || props.postError;

      $toast.error(t(errorMessage));
    }
    Sentry.captureException(error);
  }
  sending.value = false;
}

const messageAdd = (data) => {
  if (data.id === props.parent.id && data.type === props.type) {
    addMessage(data.message);
    selected.value = data.message.id;
    if (props.parent.id !== auth.idSupplier || props.type !== CHAT_MESSAGE_TYPE.SUPPLIER) {
      scrollToSelected();
    }
  }
};

const reloadLocale = async () => {
  messages.value.items.splice(0, messages.value.items.length);
  _.assignIn(pagination.value, { page: 1, pageCount: 1, itemsPerPage: 20, itemCount: 0 });
  await reload();
};

onServerPrefetch(async () => {
  await reload();
})

onMounted(async () => {
  if ((ui.displayModal || [CHAT_MESSAGE_TYPE.CART, CHAT_MESSAGE_TYPE.SUPPLIER].includes(props.type)) && (import.meta.server || navigator?.onLine)) {
    messages.value.items.splice(0, messages.value.items.length);
    _.assignIn(pagination.value, { page: 1, pageCount: 1, itemsPerPage: 20, itemCount: 0 });
    await reload();
  }
  if (import.meta.client && window?.location?.hash && window?.location?.hash.startsWith('#m-')) {
    selected.value = parseInt(window?.location?.hash.slice(3));
    await handleLoadSelected();
  }
  $eventBus.on('message:add', messageAdd);
  $eventBus.on('visitor:update', reloadLocale);
});

// Notification links only change the hash when the target page is already open
watch(() => route.hash, async (value) => {
  if (!import.meta.client || !value?.startsWith('#m-')) {
    return;
  }
  const id = parseInt(value.slice(3));
  if (!id || id === selected.value) {
    return;
  }
  selected.value = id;
  if (!_.find(messages.value.items, { id })) {
    await reloadLocale();
  }
  await handleLoadSelected();
});

onUnmounted(() => {
  $eventBus.off('visitor:update', reloadLocale);
  $eventBus.off('message:add', messageAdd);
});

const updateToRead = _.debounce(async () => {
  const ids = toRead.value;
  toRead.value = [];

  try {
    const data = await $fetch(props.fetchUrl, {
      method: 'PUT',
      body: { action: 'read', ids }
    });
    auth.setNotificationInfo(data);
    $eventBus.emit(`${props.type}:update`, {
      id: props.parent.id,
      unreadMessagesCount: Math.max((props.parent?.unreadMessagesCount || 0) - ids.length, 0)
    });
    _.forEach(ids, id => {
      const item = _.find(messages.value.items, { id });
      if (item) {
        item.isRead = true;
      }
    });
  } catch (error) {
    Sentry.captureException(error);
  }
}, 2000);

const canRead = computed(() => props.fetchUrl.includes('/api/v1/eshop/checkout/cart/') || props.type === CHAT_MESSAGE_TYPE.SUPPLIER);

const readMessage = (id) => {
  toRead.value.push(id);
  if (canRead.value) {
    updateToRead();
  }
}

const onCursorChanged = (value) => {
  cursorPosition.value = value;
}

</script>
